// 门店数据概况接口（standard-service）
// 反编译来源：HomePageManager + StoreDataSurveyFragment（com.ulucu.view.fragment.StoreDataSurveyFragment）
//
// 周期 → 接口：
//   时（today/hour） → /statistic/today/summary       — 今日 24 小时
//   天（daily）       → /statistic/daily/summary       — 天粒度
//   周（weekly）      → /statistic/weekly/summary      — 周粒度
//   月（monthly）     → /statistic/monthly/summary     — 月粒度
//   季（quarterly）   → /statistic/quarterly/summary   — 季粒度
//
// 8 个总数字（卡片）：/statistic/total/summary（携带环比 lastCycleValue）
// 用户配置：       /home/config/get（ home_store_data 是 JSON.stringify(string[]) ）
import { callInternal } from './internal.js';

/** 反编译 com.ulucu.model.util.HomePageMenuType — 8 大类 */
export const TYPE = {
	// 顾客分析 GuKeFenXi (101*)
	zkl:    '101001', // 总客流（精准进店客流）
	ddxgk:  '101002', // 到店新顾客
	ddlgk:  '101003', // 到店老顾客
	ddhy:   '101004', // 到店会员
	jtlsc:  '101005', // 人均停留时长
	jdzhl:  '101006', // 进店转化率
	gkhtl:  '101007', // 顾客回头率
	xfds:   '101008', // 消费单数（订单数）
	xsze:   '101009', // 销售总额
	kdj:    '101010', // 客单价
	ldl:    '101011', // 连带率
	cjl:    '101012', // 成交转化率（成交率）
	jdkl:   '101013', // 进店客流
	// 客流 KeLiu (102*)
	klzl:   '102001', // 客流总览
	gdrs:   '102002', // 过店人数
	djkl:   '102003', // 等等
	xse:    '102004',
	kdj2:   '102005',
	jdj:    '102006',
	// 优巡 YouXun (103*)
	bj_tj:  '103001', // 报警统计
	// 优掌柜 YouZhanggui (104*)
	syzds:  '104001', // 收银总单数
	ycds:   '104002',
	aijc:   '104003',
	// 车流 CheLiu (105*)
	cl_ddcl:   '105001',
	cl_pjtlsc: '105005',
	// 考评 KaoPing (106*)
	kp_tjkp:   '106001',
	// 事件 ShiJian (107*)
	sjzs:   '107001', // 事件总数
	dclsjs: '107002', // 待处理事件数
	// 人形 RenXing (108*)
	mdbfl:  '108001',
	// 精准客流 JinZhunKeLiu (109*)
	jzkl:   '109001', // 精准进店客流
	jzkl_xfds: '109002',
	jzkl_cjl:  '109003',
} as const;

export type TypeCode = (typeof TYPE)[keyof typeof TYPE];

/** 默认 8 个 — 与 iOS 截图一致 */
export const DEFAULT_TYPES: TypeCode[] = [
	TYPE.jzkl,    // 精准进店客流  109001
	TYPE.gdrs,    // 过店客流      102002
	TYPE.jdzhl,   // 进店转化率    101006
	TYPE.xfds,    // 订单数        101008
	TYPE.xsze,    // 销售总额      101009
	TYPE.cjl,     // 成交转化率    101012
	TYPE.sjzs,    // 事件总数      107001
	TYPE.dclsjs,  // 待处理事件数  107002
];

export type Period = 'hour' | 'day' | 'week' | 'month' | 'quarter';

/** total/summary item — 8 个卡片用（current + 环比） */
export interface TotalSummaryItem {
	type: string;           // type code
	value?: string;         // 当前数值
	is_percent?: '0' | '1'; // 是否百分比
	startDate?: string;
	endDate?: string;
	lastCycleStartDate?: string;
	lastCycleEndDate?: string;
	lastCycleValue?: string;
}

/** 单格点 — 字段随周期变化（daily 有 date；weekly 有 start_date/end_date/week/year；today 有 hour） */
export interface SeriesPoint {
	value: string;
	avg_value?: string;
	date?: string;          // daily
	hour?: string;          // today
	start_date?: string;    // weekly / monthly / quarterly / yearly
	end_date?: string;
	week?: string;
	year?: string;
	month?: string;
	quarter?: string;
}

/** alias kept for backward-compat */
export type HourPoint = SeriesPoint;

export interface SummaryDataItem<P> {
	type: string;
	is_percent?: '0' | '1';
	current_count?: string;
	items: P[];
}

/** 共用 envelope */
interface SummaryEnvelope<P> {
	current_count?: string;
	items: SummaryDataItem<P>[];
}

const STAND = 'standard';

/** 8 个卡片数据（含环比） */
export async function fetchTotalSummary(opts: {
	storeIds: string;
	startDate: string;
	endDate: string;
	types: string[];
}): Promise<TotalSummaryItem[]> {
	const res = await callInternal<{ items: TotalSummaryItem[] }>({
		short_name: STAND,
		action: '/statistic/total/summary',
		query: {
			store_ids: opts.storeIds,
			start_date: opts.startDate,
			end_date: opts.endDate,
			types: opts.types.join(','),
		},
	});
	return res.code === 0 ? (res.data?.items ?? []) : [];
}

/** 时序数据（按周期路由不同 endpoint） */
export async function fetchSeriesSummary(opts: {
	storeIds: string;
	period: Period;
	startDate: string;
	endDate: string;
	types: string[];
}): Promise<SummaryDataItem<SeriesPoint>[]> {
	const action = (() => {
		switch (opts.period) {
			case 'hour':    return '/statistic/today/summary';     // 不含 start/end_date
			case 'day':     return '/statistic/daily/summary';
			case 'week':    return '/statistic/weekly/summary';
			case 'month':   return '/statistic/monthly/summary';
			case 'quarter': return '/statistic/quarterly/summary';
		}
	})();
	const query: Record<string, string | number> = {
		store_ids: opts.storeIds,
		types: opts.types.join(','),
	};
	if (opts.period !== 'hour') {
		query.start_date = opts.startDate;
		query.end_date = opts.endDate;
	}
	const res = await callInternal<SummaryEnvelope<SeriesPoint>>({
		short_name: STAND,
		action,
		query,
	});
	return res.code === 0 ? (res.data?.items ?? []) : [];
}

/** 单指标排名（用于「该门店数据排行」） */
export interface StoreRankItem {
	type_id: string;
	value: string;        // 该门店本期数值
	b_avg?: string;       // 企业平均
	top?: string;         // 排名（1/2/3 → 显示金/银/铜牌）
	all_store?: string;   // 总店数
	exceed?: string;      // '0.7500' → 超过 75% 的门店
}

export async function fetchStoreRanks(opts: {
	storeId: string;
	startDate: string;
	endDate: string;
	types: string[];
}): Promise<Record<string, StoreRankItem>> {
	// 后端按 sort_type 单次只能查一个指标 → 并发拉所有
	const results = await Promise.all(
		opts.types.map(async (type) => {
			try {
				const res = await callInternal<StoreRankItem>({
					short_name: STAND,
					action: '/statics/store/rank',
					query: {
						store_id: opts.storeId,
						start_date: opts.startDate,
						end_date: opts.endDate,
						sort_type: type,
					},
				});
				return res.code === 0 && res.data ? [type, res.data] as const : null;
			} catch {
				return null;
			}
		}),
	);
	const map: Record<string, StoreRankItem> = {};
	for (const r of results) if (r) map[r[0]] = r[1];
	return map;
}

/** 用户的 8 项指标配置 */
export async function fetchHomeConfig(): Promise<TypeCode[] | null> {
	const res = await callInternal<{
		home_store_data?: string;
	}>({
		short_name: STAND,
		action: '/home/config/get',
	});
	if (res.code !== 0 || !res.data?.home_store_data) return null;
	try {
		const arr = JSON.parse(res.data.home_store_data) as string[];
		return arr.length > 0 ? (arr as TypeCode[]) : null;
	} catch {
		return null;
	}
}

/** 保存用户配置 — 反编译 StoreDataSurveySettingActivity.post2Server */
export async function saveHomeConfig(types: string[]): Promise<{ ok: boolean; msg?: string }> {
	const res = await callInternal({
		short_name: STAND,
		action: '/home/config/set',
		method: 'POST',
		body: { home_store_data: JSON.stringify(types) },
	});
	return { ok: res.code === 0, msg: res.msg };
}

/** home_app_module section（动态报表的高级设置 — 反编译 SortJsonEntity） */
export interface HomeAppSection {
	tag: string;                // '101'/'103'/...
	name: string;
	order: string;
	ableSetting?: string;
	compareType?: string;
	dateType?: string;
	relationWidget?: string;
	algorithmModel: string[];   // 子级 AI 模型 id 列表（仅 tag=103 悠巡使用）
	sub: { key: string; name: string; tag: string; unit?: string; order?: string }[];
}

export async function fetchHomeAppModule(): Promise<HomeAppSection[] | null> {
	const res = await callInternal<{ home_app_module?: string }>({
		short_name: STAND,
		action: '/home/config/get',
	});
	if (res.code !== 0 || !res.data?.home_app_module) return null;
	try {
		return JSON.parse(res.data.home_app_module) as HomeAppSection[];
	} catch {
		return null;
	}
}

/** 保存 home_app_module（顶层模块顺序 + 子级模型订阅） */
export async function saveHomeAppModule(modules: HomeAppSection[]): Promise<{ ok: boolean; msg?: string }> {
	const res = await callInternal({
		short_name: STAND,
		action: '/home/config/set',
		method: 'POST',
		body: { home_app_module: JSON.stringify(modules) },
	});
	return { ok: res.code === 0, msg: res.msg };
}

/** Web 客户端通用配置（home_web_data 字段，server 透明存任意 JSON 字符串）
 *  我们用来存工作台 app 顺序、其他 web-only 偏好。 */
export interface HomeWebData {
	workbenchApps?: string[];   // 工作台首页应用 keys 顺序
}

export async function fetchHomeWebData(): Promise<HomeWebData | null> {
	const res = await callInternal<{ home_web_data?: string }>({
		short_name: STAND,
		action: '/home/config/get',
	});
	if (res.code !== 0 || !res.data?.home_web_data) return null;
	try {
		return JSON.parse(res.data.home_web_data) as HomeWebData;
	} catch {
		return null;
	}
}

export async function saveHomeWebData(data: HomeWebData): Promise<{ ok: boolean; msg?: string }> {
	const res = await callInternal({
		short_name: STAND,
		action: '/home/config/set',
		method: 'POST',
		body: { home_web_data: JSON.stringify(data) },
	});
	return { ok: res.code === 0, msg: res.msg };
}
